import {
  AppWindow,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Globe,
  Keyboard,
  Mouse,
  Trash2,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { Schemas } from "@/api/client";
import type { TimelineSlot } from "@/api/queries";
import { ActivityBar } from "@/components/common/ActivityBar";
import { CategoryDot } from "@/components/common/Category";
import { CopyButton } from "@/components/common/CopyButton";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { formatDuration, formatTime } from "@/lib/format";

import { DeleteScreenshotDialog } from "./DeleteScreenshotDialog";

type Props = {
  slots: TimelineSlot[];
  index: number;
  onIndexChange: (i: number) => void;
  onClose: () => void;
  projects: Schemas["ProjectRef"][];
  timeZone?: string;
  /** Administrators may delete screenshots together with the slot time. */
  canDelete?: boolean;
};

export function ScreenshotViewer({
  slots,
  index,
  onIndexChange,
  onClose,
  projects,
  timeZone,
  canDelete,
}: Props) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? "ru";
  // The monitor choice belongs to one slot: it resets when the slot changes.
  const [picked, setPicked] = useState({ index, monitor: 0 });
  const monitor = picked.index === index ? picked.monitor : 0;
  const setMonitor = (m: number) => setPicked({ index, monitor: m });
  const hasPrev = index > 0;
  const hasNext = index < slots.length - 1;
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (deleting) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft" && hasPrev) onIndexChange(index - 1);
      if (e.key === "ArrowRight" && hasNext) onIndexChange(index + 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, hasPrev, hasNext, onIndexChange, deleting]);

  const slot = slots[index];
  const shot = slot?.screenshots[Math.min(monitor, slot.screenshots.length - 1)];
  if (!slot || !shot) return null;
  const project = projects.find((p) => p.id === slot.project_id);

  const pct = (s: number) => (slot.tracked_seconds ? Math.round((s / slot.tracked_seconds) * 100) : 0);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="w-[min(96vw,1280px)]"
        title={t("timeline.viewer.title", { time: formatTime(shot.taken_at, lang, timeZone) })}
        description={[project?.name, slot.task_title].filter(Boolean).join(" · ")}
      >
        <div className="grid gap-0 lg:grid-cols-[minmax(0,1fr)_300px]">
          <div className="flex flex-col gap-3 bg-surface-muted p-4">
            {/* The image is served from our API with a signed link; the full view is audited. */}
            <img
              key={shot.id}
              src={shot.full_url}
              alt={t("timeline.screenshotAlt", { time: formatTime(shot.taken_at, lang, timeZone) })}
              className="max-h-[70dvh] w-full rounded-md border bg-black object-contain"
            />
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex gap-1">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={!hasPrev}
                  onClick={() => onIndexChange(index - 1)}
                >
                  <ChevronLeft /> {t("timeline.viewer.prev")}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={!hasNext}
                  onClick={() => onIndexChange(index + 1)}
                >
                  {t("timeline.viewer.next")} <ChevronRight />
                </Button>
              </div>
              {slot.screenshots.length > 1 && (
                <div className="flex gap-1">
                  {slot.screenshots.map((s, i) => (
                    <Button
                      key={s.id}
                      size="sm"
                      variant={i === monitor ? "primary" : "secondary"}
                      onClick={() => setMonitor(i)}
                    >
                      {t("timeline.viewer.monitor", { n: s.monitor_index + 1 })}
                    </Button>
                  ))}
                </div>
              )}
              <div className="flex gap-1">
                <Button asChild variant="ghost" size="sm">
                  <a href={shot.full_url} target="_blank" rel="noreferrer">
                    <ExternalLink /> {t("timeline.viewer.openFull")}
                  </a>
                </Button>
                {canDelete && !shot.deleted && (
                  <Button variant="ghost" size="sm" className="text-danger" onClick={() => setDeleting(true)}>
                    <Trash2 /> {t("timeline.viewer.delete")}
                  </Button>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-4 p-4">
            {shot.deleted && (
              <Alert variant="warning">
                <div className="flex flex-col">
                  <span>{t("timeline.viewer.deleted")}</span>
                  {shot.deleted_reason && (
                    <span className="text-xs">
                      {shot.deleted_by
                        ? t("timeline.viewer.deletedBy", {
                            name: shot.deleted_by,
                            reason: shot.deleted_reason,
                          })
                        : shot.deleted_reason}
                    </span>
                  )}
                </div>
              </Alert>
            )}
            <div className="text-sm font-medium tabular-nums">
              {formatTime(slot.started_at, lang, timeZone)} – {formatTime(slot.ended_at, lang, timeZone)} ·{" "}
              {formatDuration(slot.tracked_seconds, lang)}
            </div>
            <div className="flex flex-col gap-2">
              <span className="text-xs text-muted">{t("timeline.viewer.activity")}</span>
              <ActivityBar percent={slot.activity_percent} />
              <div className="flex justify-between text-xs text-muted">
                <span className="flex items-center gap-1">
                  <Keyboard className="size-3.5" aria-hidden="true" />
                  {t("timeline.viewer.keyboard")} {pct(slot.keyboard_seconds)}%
                </span>
                <span className="flex items-center gap-1">
                  <Mouse className="size-3.5" aria-hidden="true" />
                  {t("timeline.viewer.mouse")} {pct(slot.mouse_seconds)}%
                </span>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <span className="text-xs text-muted">{t("timeline.viewer.apps")}</span>
              <ul className="flex flex-col gap-2">
                {slot.apps.map((a, i) => (
                  <li key={i} className="flex gap-2">
                    {a.kind === "url" ? (
                      <Globe className="mt-0.5 size-3.5 shrink-0 text-muted" aria-hidden="true" />
                    ) : (
                      <AppWindow className="mt-0.5 size-3.5 shrink-0 text-muted" aria-hidden="true" />
                    )}
                    <div className="flex min-w-0 flex-1 flex-col">
                      <div className="flex justify-between gap-2">
                        <span className="flex min-w-0 items-center gap-1.5">
                          <CategoryDot category={a.category} />
                          <span className="truncate font-medium">
                            {a.kind === "url" ? a.domain : a.app_name}
                          </span>
                        </span>
                        <span className="shrink-0 text-xs tabular-nums">
                          {formatDuration(a.seconds, lang)}
                        </span>
                      </div>
                      {a.window_title && (
                        <span className="truncate text-xs text-muted">{a.window_title}</span>
                      )}
                      {a.url && (
                        <span className="flex min-w-0 items-center gap-1">
                          <span className="truncate text-xs text-muted" title={a.url}>
                            {a.url}
                          </span>
                          <CopyButton value={a.url} label={t("timeline.copyUrl")} />
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </DialogContent>
      {deleting && (
        <DeleteScreenshotDialog
          screenshotId={shot.id}
          range={`${formatTime(slot.started_at, lang, timeZone)}–${formatTime(slot.ended_at, lang, timeZone)}`}
          onClose={() => setDeleting(false)}
          onDeleted={onClose}
        />
      )}
    </Dialog>
  );
}
